import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Clock, Package, RefreshCw, Truck, Search, Layers, CheckCircle2 } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';
import ViewSwitch from '@/pages/Tooling/ViewSwitch';

/**
 * Tooling on order, longest wait first.
 *
 * A plate and a die are both made outside, so this screen records the two
 * moments nobody on the floor can scan: it went out, and it came back. The tabs
 * are the states it passes through — Pending, Ordered, Received — with All
 * last, the way the job cards and orders screens are laid out.
 *
 * Tooling is ordered from one vendor in batches, so the action this exists for
 * is "these six went out today". Rows can be ticked and marked together rather
 * than clicked one at a time down the Action column.
 *
 * The screen is the same for both kinds, so it is written once and handed a
 * `tooling` from `toolings.js` — the web-side twin of the backend's one router
 * built twice.
 */

const STATUS = {
  pending: { label: 'Pending', className: 'text-amber-700' },
  ordered: { label: 'Ordered', className: 'text-blue-700' },
  received: { label: 'Received', className: 'text-emerald-700' },
};

// "1 die", "6 dies" — the label the screen was handed, counted
const noun = (tooling, n) => (n === 1 ? tooling.label.toLowerCase() : tooling.plural);

const SORTS = [
  ['days_waiting', 'Longest waiting'],
  ['order_date', 'Order date'],
  ['delivery_date', 'Delivery date'],
];

export default function TrackingScreen({ tooling }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(searchParams.get('tab') || 'pending');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchDebounce, setSearchDebounce] = useState(search);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [canMark, setCanMark] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('days_waiting');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => new Set());

  // All sits last: the working tabs come first, the archive behind them
  const tabs = useMemo(() => ([
    { key: 'pending', label: 'Pending', icon: Clock, tone: 'warn' },
    { key: 'ordered', label: 'Ordered', icon: Package },
    { key: 'received', label: 'Received', icon: CheckCircle2, tone: 'good' },
    { key: 'all', label: `All ${tooling.plural}`, icon: Layers },
  ]), [tooling]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [tab, searchDebounce]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, status: tab };
      if (searchDebounce) params.search = searchDebounce;
      const res = await api.get(tooling.trackingApi, { params });
      setRows(res.data[tooling.plural] || []);
      setCounts(res.data.counts || {});
      setCanMark(res.data.can_mark);
      setTotal(res.data.total || 0);
      setPicked(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load ${tooling.kind} tracking`);
    }
    setLoading(false);
  }, [page, sortBy, tab, searchDebounce, tooling]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (tab !== 'pending') params.set('tab', tab);
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [tab, search, setSearchParams]);

  const mark = useCallback(async (list, action) => {
    if (list.length === 0) return;
    setBusy(true);
    const results = await Promise.allSettled(
      list.map(row => api.post(tooling.markApi(row, action), {}))
    );
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed) {
      toast.error(`${failed} of ${list.length} could not be marked ${action}`);
    } else {
      toast.success(`${list.length} ${noun(tooling, list.length)} marked ${action}`);
    }
    setBusy(false);
    fetchRows();
  }, [fetchRows, tooling]);

  const togglePick = (id) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const pickedRows = useMemo(
    () => rows.filter(r => picked.has(r.line_item_id)),
    [rows, picked]
  );
  const canOrder = pickedRows.filter(r => r[tooling.statusField] === 'pending');
  const canReceive = pickedRows.filter(r => r[tooling.statusField] === 'ordered');
  const pickable = rows.filter(r => r[tooling.statusField] !== 'received');

  const columns = useMemo(() => ([
    ...(canMark ? [{
      key: 'pick',
      header: '',
      width: '2.25rem',
      render: (row) => row[tooling.statusField] === 'received' ? null : (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={picked.has(row.line_item_id)}
            onCheckedChange={() => togglePick(row.line_item_id)}
            aria-label={`Select ${row.item_name || row.order_id}`}
            className="h-3.5 w-3.5"
          />
        </span>
      ),
    }] : []),
    {
      key: 'item',
      header: 'Item / Order',
      width: 'minmax(12rem,1.4fr)',
      render: (row) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{row.item_name || '—'}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{row.order_id}</span>
        </>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(10rem,1fr)',
      render: (row) => (
        <>
          <span className="block truncate text-slate-700">{row.customer_name}</span>
          {row.firm_name && <span className="block truncate text-[11px] leading-tight text-slate-400">{row.firm_name}</span>}
        </>
      ),
    },
    {
      key: 'product_type',
      header: 'Type',
      width: '7rem',
      mobile: false,
      render: (row) => <span className="block truncate text-[11px] text-slate-600">{row.product_type}</span>,
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (row) => <span className="font-medium">{row.quantity?.toLocaleString() || '—'}</span>,
      total: (list) => list.reduce((s, r) => s + (r.quantity || 0), 0).toLocaleString(),
    },
    {
      key: tooling.statusField,
      header: 'Status',
      width: '7.5rem',
      render: (row) => {
        const s = STATUS[row[tooling.statusField]] || STATUS.pending;
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className={`text-[12px] font-medium ${s.className}`}>{s.label}</span>
            {/* the job is standing at the stage, not merely waiting on the tooling */}
            {row.holding_production && row[tooling.statusField] !== 'received' && (
              <span className="shrink-0 rounded bg-red-50 px-1 text-[9px] font-semibold text-red-600">
                holding
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: tooling.orderedAtField,
      header: 'Ordered',
      width: '9.5rem',
      mobile: false,
      render: (row) => row[tooling.orderedAtField] ? (
        <span className="block truncate text-[11px] text-slate-600"
          title={row[tooling.orderedByField] ? `by ${row[tooling.orderedByField]}` : undefined}>
          {formatIST(row[tooling.orderedAtField], { compact: true })}
        </span>
      ) : <span className="text-[11px] text-slate-400">—</span>,
    },
    {
      key: tooling.receivedAtField,
      header: 'Received',
      width: '9.5rem',
      mobile: false,
      render: (row) => row[tooling.receivedAtField] ? (
        <span className="block truncate text-[11px] text-slate-600"
          title={row[tooling.receivedByField] ? `by ${row[tooling.receivedByField]}` : undefined}>
          {formatIST(row[tooling.receivedAtField], { compact: true })}
        </span>
      ) : <span className="text-[11px] text-slate-400">—</span>,
    },
    {
      key: 'days_waiting',
      header: 'Waiting',
      align: 'right',
      width: '5rem',
      render: (row) => (row[tooling.statusField] !== 'received' && row.days_waiting > 0) ? (
        <span className={`font-semibold ${
          row.days_waiting > 3 ? 'text-red-600' : row.days_waiting > 1 ? 'text-amber-600' : 'text-slate-600'
        }`}>
          {row.days_waiting}d
        </span>
      ) : <span className="text-slate-400">—</span>,
    },
    {
      key: 'delivery_date',
      header: 'Due',
      width: '6.5rem',
      mobile: false,
      render: (row) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {row.delivery_date ? formatIST(row.delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    ...(canMark ? [{
      key: 'actions',
      header: '',
      width: '9rem',
      align: 'right',
      render: (row) => (
        <span className="flex items-center justify-end gap-0.5">
          {row[tooling.statusField] === 'pending' && (
            <CellButton
              icon={Package}
              label="Mark ordered"
              disabled={busy}
              onClick={() => mark([row], 'ordered')}
            />
          )}
          {row[tooling.statusField] === 'ordered' && (
            <CellButton
              icon={Truck}
              label="Mark received"
              tone="good"
              disabled={busy}
              onClick={() => mark([row], 'received')}
            />
          )}
          {row[tooling.statusField] === 'received' && (
            <span className="px-1 text-[11px] font-medium text-emerald-600">Done</span>
          )}
        </span>
      ),
    }] : []),
  ]), [picked, busy, mark, canMark, tooling]);

  const totalPages = Math.ceil(total / 50);

  return (
    <Screen data-testid={`${tooling.kind}-tracking-page`}>
      <CommandBar
        title={tooling.boardTitle}
        subtitle={picked.size ? `${picked.size} selected` : `${total} ${noun(tooling, total)}`}
        actions={[
          canOrder.length > 0 && {
            key: 'bulk-order',
            label: `Mark ${canOrder.length} ordered`,
            icon: Package,
            tone: 'primary',
            disabled: busy,
            onClick: () => mark(canOrder, 'ordered'),
          },
          canReceive.length > 0 && {
            key: 'bulk-receive',
            label: `Mark ${canReceive.length} received`,
            icon: Truck,
            tone: 'good',
            disabled: busy,
            onClick: () => mark(canReceive, 'received'),
          },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchRows, disabled: loading },
        ]}
      >
        <ViewSwitch tooling={tooling} current="board" />
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-9 pl-8 text-sm sm:h-7 sm:text-xs"
            placeholder="Order, customer, firm…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label={`Sort ${tooling.plural} by`}
          className="h-9 rounded border border-slate-200 bg-white px-2 text-sm text-slate-700 sm:h-7 sm:text-xs"
        >
          {SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </CommandBar>

      <StatStrip
        stats={tabs.map(t => ({
          key: t.key,
          label: t.label,
          value: counts[t.key] ?? 0,
          tone: t.key === 'pending' && counts.pending ? 'warn'
            : t.key === 'received' ? 'good' : 'default',
        }))}
        activeKey={tab}
        onSelect={(key) => setTab(key || 'pending')}
      >
        {counts.overdue > 0 && (
          <span className="flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-[11px] font-medium text-red-600">
            {counts.overdue} waiting over 3d
          </span>
        )}
      </StatStrip>

      <Pane
        title={tooling.labelPlural}
        aside={tabs.find(t => t.key === tab)?.label.toLowerCase() || 'all'}
        scroll={false}
        bodyClassName="flex flex-col"
        actions={
          <span className="flex items-center gap-2">
            {canMark && pickable.length > 0 && (
              <button
                type="button"
                onClick={() => setPicked(picked.size ? new Set() : new Set(pickable.map(r => r.line_item_id)))}
                className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20"
              >
                {picked.size ? 'Clear' : 'Select all'}
              </button>
            )}
            {totalPages > 1 && (
              <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
                {page} / {totalPages}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
              </span>
            )}
          </span>
        }
      >
        <DataGrid
          data-testid={`${tooling.plural}-table`}
          columns={columns}
          rows={rows}
          loading={loading}
          getRowId={(row) => row.line_item_id}
          onRowClick={(row) => navigate(`/orders/${row.order_uuid}`)}
          showTotals={rows.length > 0}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Clock className="h-8 w-8 text-slate-300" />
              {tab === 'pending' ? tooling.emptyBoard : `No ${tooling.plural} here`}
            </span>
          }
        />
      </Pane>
    </Screen>
  );
}
